// Role → sidebar menu items
// Each entry has a `key` matching the backend TABS_BY_STATION so we can filter
// by user.permissions for Manager/Employee accounts.
export const ROLE_LABEL = {
  super_admin: "Super Admin",
  back_office: "Back Office",
  factory_1: "Factory 1",
  factory_2: "Factory 2",
  factory_3: "Factory 3",
  packing_orders: "Packing Orders",
  inventory_packing: "Inventory Packing",
  shipping_order: "Shipping Order",
  three_pl: "3PL Warehouse (USA)",
};

export const SUB_ROLE_LABEL = {
  administrator: "Administrator",
  manager: "Manager",
  employee: "Employee",
};

export const STAGE_LABEL = {
  lead: "Lead",
  pre_order: "Pre Orders",
  pending: "Pending Orders",
  advance: "Advance Orders",
  production: "Production Orders",
  product_transfer: "Product Transfer",
  packing: "Packing Orders",
  inventory_packing: "Inventory Packing",
  pickup_3pl: "3PL Pick-up",
  shipping: "Shipping Orders",
  completed: "Orders Received",
  fulfilled: "Order Fulfilled",
  in_stock: "In Stock",
  stock_used: "Stock Used",
  na: "NA Orders",
  flagged: "Flagged Orders",
  abandoned: "Abandoned Orders",
  cancelled: "Cancelled Orders",
  no_mold: "No Mold",
};

export const STAGE_BADGE = {
  lead: "neutral",
  pre_order: "neutral",
  pending: "blue",
  advance: "blue",
  production: "warning",
  product_transfer: "warning",
  packing: "warning",
  inventory_packing: "warning",
  pickup_3pl: "warning",
  shipping: "blue",
  completed: "success",
  fulfilled: "success",
  in_stock: "success",
  stock_used: "neutral",
  na: "neutral",
  flagged: "critical",
  abandoned: "neutral",
  cancelled: "critical",
  no_mold: "critical",
};

/**
 * Singular-form, uppercase labels used ONLY on the Order Detail header
 * (big stage pill). Different from STAGE_LABEL — which is plural for
 * sidebar/list-page titles. Business rule Feb 28, 2026: every stage
 * header reads clearly on its own (e.g. "IN PRODUCTION", "IN PACKING")
 * so BO can see the stage from across the desk.
 */
export const STAGE_HEADER = {
  lead:              "LEAD",
  pre_order:         "PRE-ORDER",
  pending:           "PENDING",
  advance:           "ADVANCE",
  production:        "IN PRODUCTION",
  product_transfer:  "IN TRANSFER",
  packing:           "IN PACKING",
  inventory_packing: "INV PACKING",
  pickup_3pl:        "3PL PICK-UP",
  shipping:          "SHIPPING",
  completed:         "RECEIVED",
  fulfilled:         "FULFILLED",
  na:                "NA",
  flagged:           "FLAGGED",
  abandoned:         "ABANDONED",
  cancelled:         "CANCELLED",
  no_mold:           "NO MOLD",
  converted:         "CONVERTED → PO",
  on_hold:           "ON HOLD · QC ISSUE",
  quality_control:   "QUALITY CONTROL",
};

/**
 * Big colored pill classes per pipeline stage — used by the Order Detail
 * header. Each stage gets a distinct emerald/amber/red/etc. so the stage
 * reads unambiguously at any distance. Border is thicker (border-2) for
 * emphasis; keep font-weight black.
 */
export const STAGE_PILL = {
  lead:              "border-2 border-teal-500 bg-teal-50 text-teal-800",
  pre_order:         "border-2 border-sky-500 bg-sky-50 text-sky-800",
  pending:           "border-2 border-amber-500 bg-amber-50 text-amber-800",
  advance:           "border-2 border-indigo-500 bg-indigo-50 text-indigo-800",
  production:        "border-2 border-emerald-500 bg-emerald-50 text-emerald-800",
  product_transfer:  "border-2 border-lime-500 bg-lime-50 text-lime-800",
  packing:           "border-2 border-cyan-500 bg-cyan-50 text-cyan-800",
  inventory_packing: "border-2 border-orange-500 bg-orange-50 text-orange-800",
  pickup_3pl:        "border-2 border-stone-600 bg-stone-100 text-stone-900",
  shipping:          "border-2 border-violet-500 bg-violet-50 text-violet-800",
  completed:         "border-2 border-slate-500 bg-slate-100 text-slate-800",
  fulfilled:         "border-2 border-emerald-600 bg-emerald-100 text-emerald-900",
  na:                "border-2 border-red-500 bg-red-50 text-red-800",
  flagged:           "border-2 border-yellow-500 bg-yellow-50 text-yellow-800",
  abandoned:         "border-2 border-neutral-500 bg-neutral-100 text-neutral-800",
  cancelled:         "border-2 border-red-600 bg-red-100 text-red-900",
  no_mold:           "border-2 border-rose-600 bg-rose-100 text-rose-900",
  converted:         "border-2 border-teal-600 bg-teal-100 text-teal-900",
  on_hold:           "border-2 border-amber-600 bg-amber-100 text-amber-900",
  quality_control:   "border-2 border-red-700 bg-red-600 text-white",
};

// Production sub-status — single source of truth for BOTH the list "Stage"
// pill and the Order Detail header pill so they always match.
export const PRODUCTION_SUBSTAGE = {
  awaiting: "Awaiting Signature",
  to_print: "To Print",
  in_production: "In Production",
  signed_off: "Complete Sign-off",
};
export const PRODUCTION_SUBSTAGE_BADGE = {
  awaiting: "bg-amber-50 text-amber-700 border-amber-200 border",
  to_print: "bg-violet-50 text-violet-700 border-violet-200 border",
  in_production: "bg-emerald-50 text-emerald-700 border-emerald-200 border",
  signed_off: "bg-blue-50 text-blue-700 border-blue-200 border",
};
export const PRODUCTION_SUBSTAGE_PILL = {
  awaiting: "border-2 border-amber-500 bg-amber-50 text-amber-800",
  to_print: "border-2 border-violet-500 bg-violet-50 text-violet-800",
  in_production: "border-2 border-emerald-500 bg-emerald-50 text-emerald-800",
  signed_off: "border-2 border-blue-500 bg-blue-50 text-blue-800",
};
// awaiting → (sign) → to_print → (print sheet) → in_production → (sign off) → signed_off
// Packed-but-not-shipped orders keep status "inventory_packing" / "packing"
// (the station), but every header, badge and search tag must SHOW the state
// the packer stamped — mirrors the list badges (PO-5058, Oct 7, 2026).
export function packedStageOverride(o) {
  if (o?.status === "inventory_packing" && o?.inventory_packed_at) {
    return {
      label: o.is_pickup ? "READY FOR PICK UP" : "READY TO BE SHIPPED", icon: o.is_pickup ? "🏬" : "✅",
      pill: "border-2 border-emerald-600 bg-emerald-500 text-white", badge: "bg-emerald-500 text-white border border-emerald-600",
    };
  }
  if (o?.status === "packing" && o?.packed_at) {
    return { label: "COMPLETE PACKING", icon: "✓", pill: "border-2 border-emerald-500 bg-emerald-50 text-emerald-800", badge: "bg-emerald-50 text-emerald-700 border border-emerald-200" };
  }
  if (o?.status === "shipping") {
    // Factory → warehouse leg. "SHIPPED" is reserved for the client leg (user rule Oct 9, 2026).
    if (o.received_at) return { label: "RECEIVED", icon: "✓", pill: "border-2 border-emerald-500 bg-emerald-50 text-emerald-800", badge: "bg-emerald-50 text-emerald-700 border border-emerald-200" };
    if (o.shipping_tracking_number) return { label: "IN TRANSIT TO WAREHOUSE", icon: "🚚", pill: "border-2 border-blue-500 bg-blue-50 text-blue-800", badge: "bg-blue-50 text-blue-700 border border-blue-200" };
    return { label: "PENDING SHIPPING", icon: "⏳", pill: "border-2 border-amber-500 bg-amber-50 text-amber-800", badge: "bg-amber-50 text-amber-700 border border-amber-200" };
  }
  if (o?.status === "fulfilled" && o.shipping_tracking_number && o.fulfilled_method !== "pickup_ready") {
    return { label: "SHIPPED", icon: "🚚", pill: "border-2 border-blue-500 bg-blue-50 text-blue-800", badge: "bg-blue-50 text-blue-700 border border-blue-200" };
  }
  return null;
}

export function productionSubstage(o) {
  if (o?.production_completed_by && o?.status !== "production") return "signed_off";
  if (!o?.factory_signature) return "awaiting";
  if (!o?.production_printed_at) return "to_print";
  return "in_production";
}

// Mirrors backend _production_entered_at / _production_deadline (7 days + approved extensions).
export const PRODUCTION_DEADLINE_DAYS = 7;
export function productionEnteredAt(o) {
  if (o?.production_entered_at !== undefined) return o.production_entered_at || null; // list rows carry it pre-computed
  let entered = null;
  for (const h of o?.status_history || []) {
    if (h.status === "production") { if (!entered) entered = h.at; }
    else if (h.status !== "on_hold") entered = null;
  }
  return entered || o?.production_started_at || o?.factory_signed_at || null;
}
export function productionDeadline(o) {
  const entered = productionEnteredAt(o);
  if (!entered) return null;
  const d = new Date(entered);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + PRODUCTION_DEADLINE_DAYS + Number(o?.production_extension_days || 0));
  return d;
}

export const SIDEBAR = {
  super_admin: [
    { key: "dashboard", to: "/", label: "Dashboard", icon: "LayoutDashboard" },
    { key: "rush", to: "/rush", label: "Rush queue" },
    { key: "customers", to: "/customers", label: "Customers", icon: "Users" },
    { key: "orders_pre_order", to: "/orders/pre_order", label: "Pre Orders", children: [
      { key: "orders_abandoned", to: "/orders/abandoned", label: "Abandoned Orders" },
    ]},
    { key: "orders_pending", to: "/orders/pending", label: "Pending Orders" },
    { key: "orders_advance", to: "/orders/advance", label: "Advance Orders", children: [
      { key: "orders_approved_leads", to: "/orders/approved-leads", label: "Approved Leads" },
      { key: "orders_na", to: "/orders/na", label: "NA Orders" },
      { key: "orders_flagged", to: "/orders/flagged", label: "Flagged Orders" },
      { key: "orders_cancelled", to: "/orders/cancelled", label: "Cancelled Orders" },
      { key: "orders_no_mold", to: "/orders/no_mold", label: "No Mold" },
    ]},
    { key: "orders_inventory_packing", to: "/orders/inventory_packing", label: "Inv Packing" },
    { key: "orders_production", to: "/orders/production", label: "Production Orders", children: [
      { key: "orders_product_transfer", to: "/orders/product_transfer", label: "Product Transfer" },
    ]},
    { key: "orders_packing", to: "/orders/packing", label: "Packing Orders", children: [
      { key: "orders_ready_to_ship", to: "/orders/ready-to-ship", label: "Ready to Ship" },
    ]},
    { key: "orders_shipping", to: "/orders/shipping", label: "Shipping Orders", children: [
      { key: "orders_completed", to: "/orders/completed", label: "Orders Received" },
    ]},
    { key: "orders_fulfilled", to: "/orders/fulfilled", label: "Order Fulfilled" },
    { key: "claims", to: "/claims", label: "Claims" },
    { key: "installers", to: "/installers", label: "Installers" },
    { key: "inventory", to: "/inventory", label: "Inventory" },
    { key: "report", to: "/report", label: "Report" },
    { key: "batch_orders", to: "/batch-orders", label: "Batch Orders" },
    { key: "billing", to: "/billing", label: "Billing" },
    { key: "users", to: "/users", label: "Users" },
    { key: "team", to: "/team", label: "Team" },
    { key: "settings", to: "/settings", label: "Settings" },
  ],
  back_office: [
    { key: "dashboard", to: "/", label: "Dashboard" },
    { key: "rush", to: "/rush", label: "Rush queue" },
    { key: "customers", to: "/customers", label: "Customers" },
    { key: "orders_pre_order", to: "/orders/pre_order", label: "Pre Orders", children: [
      { key: "orders_abandoned", to: "/orders/abandoned", label: "Abandoned Orders" },
    ]},
    { key: "orders_pending", to: "/orders/pending", label: "Pending Orders" },
    { key: "orders_advance", to: "/orders/advance", label: "Advance Orders", children: [
      { key: "orders_approved_leads", to: "/orders/approved-leads", label: "Approved Leads" },
      { key: "orders_na", to: "/orders/na", label: "NA Orders" },
      { key: "orders_flagged", to: "/orders/flagged", label: "Flagged Orders" },
      { key: "orders_cancelled", to: "/orders/cancelled", label: "Cancelled Orders" },
      { key: "orders_no_mold", to: "/orders/no_mold", label: "No Mold" },
    ]},
    { key: "orders_inventory_packing", to: "/orders/inventory_packing", label: "Inv Packing" },
    { key: "orders_production", to: "/orders/production", label: "Production Orders", children: [
      { key: "orders_product_transfer", to: "/orders/product_transfer", label: "Product Transfer" },
    ]},
    { key: "orders_packing", to: "/orders/packing", label: "Packing Orders", children: [
      { key: "orders_ready_to_ship", to: "/orders/ready-to-ship", label: "Ready to Ship" },
    ]},
    { key: "orders_shipping", to: "/orders/shipping", label: "Shipping Orders", children: [
      { key: "orders_completed", to: "/orders/completed", label: "Orders Received" },
    ]},
    { key: "orders_fulfilled", to: "/orders/fulfilled", label: "Order Fulfilled" },
    { key: "claims", to: "/claims", label: "Claims" },
    { key: "installers", to: "/installers", label: "Installers" },
    { key: "inventory", to: "/inventory", label: "Inventory" },
    { key: "report", to: "/report", label: "Report" },
    { key: "batch_orders", to: "/batch-orders", label: "Batch Orders" },
    { key: "team", to: "/team", label: "Team" },
    { key: "settings", to: "/settings", label: "Settings" },
  ],
  factory_1: [
    { key: "dashboard", to: "/", label: "Dashboard" },
    { key: "rush", to: "/rush", label: "Rush queue" },
    { key: "orders_pending", to: "/orders/pending", label: "Pending Orders" },
    { key: "orders_advance", to: "/orders/advance", label: "Advance Orders" },
    { key: "orders_production", to: "/orders/production", label: "Production" },
    { key: "orders_product_transfer", to: "/orders/product_transfer", label: "Product Transfer" },
    { key: "orders_completed", to: "/orders/completed", label: "Completed" },
    { key: "claims", to: "/claims", label: "Claims" },
    { key: "billing", to: "/billing", label: "Billing" },
    { key: "team", to: "/team", label: "Team" },
  ],
  packing_orders: [
    { key: "dashboard", to: "/", label: "Dashboard" },
    { key: "orders_product_transfer", to: "/orders/product_transfer", label: "Product Transfer" },
    { key: "orders_packing", to: "/orders/packing", label: "Packing Orders", children: [
      { key: "orders_ready_to_ship", to: "/orders/ready-to-ship", label: "Ready to Ship" },
    ]},
    { key: "team", to: "/team", label: "Team" },
  ],
  inventory_packing: [
    { key: "dashboard", to: "/", label: "Dashboard" },
    { key: "orders_completed", to: "/orders/completed", label: "Orders Received" },
    { key: "orders_inventory_packing", to: "/orders/inventory_packing", label: "Inventory Packing" },
    { key: "inventory", to: "/inventory", label: "Inventory" },
    { key: "team", to: "/team", label: "Team" },
  ],
  shipping_order: [
    { key: "dashboard", to: "/", label: "Dashboard" },
    { key: "orders_shipping", to: "/orders/shipping", label: "Shipping Orders" },
    { key: "orders_completed", to: "/orders/completed", label: "Orders Received" },
    { key: "team", to: "/team", label: "Team" },
  ],
  three_pl: [
    { key: "dashboard", to: "/", label: "Dashboard" },
    { key: "orders_pickup_3pl", to: "/three-pl", label: "3PL Pick-up" },
    { key: "team", to: "/team", label: "Team" },
  ],
  factory_2: [
    { key: "dashboard", to: "/", label: "Dashboard" },
    { key: "rush", to: "/rush", label: "Rush queue" },
    { key: "orders_pending", to: "/orders/pending", label: "Pending Orders" },
    { key: "orders_advance", to: "/orders/advance", label: "Advance Orders" },
    { key: "orders_production", to: "/orders/production", label: "Production" },
    { key: "orders_product_transfer", to: "/orders/product_transfer", label: "Product Transfer" },
    { key: "orders_completed", to: "/orders/completed", label: "Completed" },
    { key: "billing", to: "/billing", label: "Billing" },
    { key: "team", to: "/team", label: "Team" },
  ],
  factory_3: [
    { key: "dashboard", to: "/", label: "Dashboard" },
    { key: "rush", to: "/rush", label: "Rush queue" },
    { key: "orders_pending", to: "/orders/pending", label: "Pending Orders" },
    { key: "orders_advance", to: "/orders/advance", label: "Advance Orders" },
    { key: "orders_production", to: "/orders/production", label: "Production" },
    { key: "orders_product_transfer", to: "/orders/product_transfer", label: "Product Transfer" },
    { key: "orders_completed", to: "/orders/completed", label: "Completed" },
    { key: "billing", to: "/billing", label: "Billing" },
    { key: "team", to: "/team", label: "Team" },
  ],
};

// Filter a sidebar list based on the user's sub_role + permissions map.
// - Super admin & Administrator sub_role: see everything
// - Manager & Employee: see items where permissions[key] === true, PLUS the
//   Team item (managers can always view their team). If permissions is null
//   (unrestricted), everything is visible.
// The "team" tab is always visible for administrator + manager (they need it
// to manage their team even if a peer admin removed it from permissions).
export function filterSidebar(items, user) {
  if (!user) return [];
  const out = (user.role === "super_admin" || user.sub_role === "administrator" || !user.sub_role)
    ? items
    : (() => {
        const perms = user.permissions;
        const allow = (it) => {
          if (it.key === "team" && user.sub_role === "manager") return true;
          if (perms == null) return true;
          return perms[it.key] === true;
        };
        return items
          .filter(allow)
          .map((it) => it.children ? { ...it, children: it.children.filter(allow) } : it);
      })();
  return groupUnderSettings(out);
}

// Users + Team live under the Settings drop-list wherever a station has a
// Settings tab (user rule Sep 29, 2026). Stations without Settings keep them flat.
export function groupUnderSettings(items) {
  const settings = items.find((it) => it.key === "settings");
  if (!settings) return items;
  const nested = items.filter((it) => it.key === "users" || it.key === "team");
  if (!nested.length) return items;
  return items
    .filter((it) => it.key !== "users" && it.key !== "team")
    .map((it) => it.key === "settings" ? { ...it, children: [...(it.children || []), ...nested] } : it);
}
